import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Brain,
  Heart,
  Dice5,
  Bot,
  Shield,
  Sparkles,
  MessageSquare,
  Info,
  Crown,
  AlertTriangle,
  Flame,
  Music
} from 'lucide-react';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-radial from-[#fbd3e9] via-[#a1c4fd] to-[#c2e9fb]">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#a1c4fd]/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[#fbd3e9]/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 relative">
          {/* AI Badge */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="inline-flex items-center gap-2 backdrop-blur-xl bg-white/30 px-4 py-2 rounded-full border border-white/20">
              <Bot size={16} className="text-sky-500" />
              <span className="text-slate-700">Boosté par l'IA</span>
            </div>
          </div>

          <div className="text-center max-w-4xl mx-auto mb-16">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-wide">
              <span className="block bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                Bienvenue dans
              </span>
              <span className="block bg-gradient-to-r from-[#a1c4fd] to-[#bb377d] bg-clip-text text-transparent">
                l'univers Czen
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Une suite d'applications IA pour t'accompagner dans ta vie personnelle
            </p>
          </div>

          {/* Main Actions */}
          <div className="space-y-6 max-w-5xl mx-auto mb-20">
            {/* First row - 4 cards */}
            <div className="grid md:grid-cols-4 gap-6">
            <Link 
              to="/context"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-sky-400 to-sky-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Brain className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">Médiation IA</h3>
              <p className="text-slate-600 mb-4">Résous tes conflits avec bienveillance</p>
              <div className="flex items-center text-sky-500 font-medium">
                Commencer
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <a 
              href="https://love.czen.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-rose-400 to-rose-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Heart className="text-white" size={24} />
              </div>
              <div className="absolute top-2 left-2 bg-rose-500 text-white text-xs px-2 py-1 rounded-full">
                Nouvelle App !
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">Compatibilité</h3>
              <p className="text-slate-600 mb-4">Teste ton alchimie sexuelle</p>
              <div className="flex items-center text-rose-500 font-medium">
                Découvrir
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <a 
              href="https://aouv.czen.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-violet-400 to-violet-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Dice5 className="text-white" size={24} />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">Action ou Vérité</h3>
              <p className="text-slate-600 mb-4">Des défis pimentés pour adultes</p>
              <div className="flex items-center text-violet-500 font-medium">
                Jouer
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <a 
              href="https://queen.czen.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-amber-400 to-amber-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Crown className="text-white" size={24} />
              </div>
              <div className="absolute top-2 left-2 bg-amber-500 text-white text-xs px-2 py-1 rounded-full">
                Nouveau !
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">Queen Score</h3>
              <p className="text-slate-600 mb-4">Révèle ton aura de Queen</p>
              <div className="flex items-center text-amber-500 font-medium">
                Découvrir
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            </div>

            {/* Second row - 3 cards */}
            <div className="grid md:grid-cols-3 gap-6">
            <a 
              href="https://limite.czen.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-red-400 to-red-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                🔥 Populaire
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">Le Jeu des Limites</h3>
              <p className="text-slate-600 mb-4">Découvre tes limites</p>
              <div className="flex items-center text-red-500 font-medium">
                Explorer
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <a 
              href="https://liberti.czen.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-400 to-orange-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Flame className="text-white" size={24} />
              </div>
              <div className="absolute top-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                Nouveau !
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">LibertiQuizz</h3>
              <p className="text-slate-600 mb-4">Jusqu'où es-tu prête à aller ?</p>
              <div className="flex items-center text-orange-500 font-medium">
                Découvrir
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            <a 
              href="https://blindtest.czen.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 p-8 rounded-2xl transition-all duration-300 hover:scale-105"
            >
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-purple-400 to-purple-500 w-12 h-12 rounded-xl flex items-center justify-center">
                <Music className="text-white" size={24} />
              </div>
              <div className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded-full">
                🎵 Fun
              </div>
              <h3 className="text-xl font-semibold mb-2 text-slate-800">Blind Test Musical</h3>
              <p className="text-slate-600 mb-4">Teste tes connaissances musicales</p>
              <div className="flex items-center text-purple-500 font-medium">
                Jouer
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>
            </div>
          </div>

          {/* Quick Access Bar */}
          <div className="max-w-5xl mx-auto mb-20 overflow-hidden">
            <h2 className="text-2xl font-semibold text-center mb-8 text-slate-800">Comment te sens-tu aujourd'hui ?</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 px-4 scrollbar-hide">
              {[
                { text: "Je veux me confier", icon: MessageSquare, color: "text-sky-500" },
                { text: "Je veux jouer", icon: Dice5, color: "text-violet-500" },
                { text: "Je veux tester notre alchimie", icon: Heart, color: "text-rose-500" },
                { text: "Je cherche des conseils", icon: Info, color: "text-emerald-500" }
              ].map((item, index) => (
                <button
                  key={index}
                  className="flex items-center gap-2 whitespace-nowrap backdrop-blur-xl bg-white/20 hover:bg-white/30 border border-white/20 px-6 py-3 rounded-full transition-all duration-300 hover:scale-105"
                >
                  <item.icon size={18} className={item.color} />
                  <span className="text-slate-700">{item.text}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                icon: Bot,
                title: "IA dernière génération",
                description: "Algorithmes de pointe pour des réponses pertinentes",
                gradient: "from-sky-400 to-sky-500"
              },
              {
                icon: Shield,
                title: "100% confidentiel",
                description: "Tes données sont cryptées et sécurisées",
                gradient: "from-emerald-400 to-emerald-500"
              },
              {
                icon: Sparkles,
                title: "Personnalisé pour toi",
                description: "Des conseils adaptés à ta situation",
                gradient: "from-violet-400 to-violet-500"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="backdrop-blur-xl bg-white/20 border border-white/20 p-6 rounded-xl transition-all duration-300 hover:scale-105"
              >
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4`}>
                  <feature.icon className="text-white" size={24} />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-slate-800">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 backdrop-blur-xl bg-white/10 border-t border-white/20">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-600 mb-4">
            Czen est un projet indépendant qui utilise l'IA pour t'aider à mieux vivre tes relations.
          </p>
          <div className="flex justify-center gap-6 text-sm text-slate-500">
            <a href="mailto:contact@woptimizer.com" className="hover:text-sky-500 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;